<script setup lang="ts">
import { SingleProgrammeQuery } from '~/queries/single/programme'

definePageMeta({ path: '/exhibitions-events/:type()/:slug()' })

const route = useRoute()
const { locale } = useI18n()

const slug = route.params.slug as string
const type = route.params.type as string

const runtimeConfig = useRuntimeConfig()
const previewSecret = runtimeConfig?.previewSecret || runtimeConfig?.public?.previewSecret
const isPreview = computed(() => route?.query?.previewSecret === previewSecret)

const { data, error } = await useAsyncData(
  `programme-${type}-${slug}-${locale.value}`,
  () => $fetch('/api/payload-gql', {
    method: 'POST',
    body: {
      query: SingleProgrammeQuery,
      variables: {
        slug,
        locale: locale.value,
        isPreview: isPreview.value,
        // with `draft: true` payload returns each doc's latest version, whatever its
        // status -- so preview must accept both, or a doc with no pending draft
        // (latest version published) matches nothing and the page renders empty
        statuses: isPreview.value ? ['draft', 'published'] : ['published'],
      },
      isPreview: isPreview.value,
    },
  })
)

if (error.value) {
  throw createError({ statusCode: 500, statusMessage: 'Error fetching programme' })
}

const programme = computed(() => (data.value as any)?.docs?.[0])

if (!programme.value && !isPreview.value) {
  throw createError({ statusCode: 404, statusMessage: 'Programme not found' })
}

if (programme.value && programme.value.type?.slug !== type) {
  throw createError({ statusCode: 404, statusMessage: 'Programme not found' })
}

usePageSeo({
  title: () => programme.value?.title,
  description: () => programme.value?.description,
  image: () => programme.value?.image,
  type: 'article',
})

const pageNav = computed(() => usePageNavigation(programme.value?.content))
</script>

<template>
  <page-layout>
    <template #header>
      <page-header
        :title="programme?.title || ''"
        :route="{
          name: 'programme-type',
          params: { type: programme?.type?.slug },
          title: programme?.type?.title,
        }"
        :parent="{
          name: 'programme',
        }"
        :page-nav="pageNav"
      />
    </template>

    <template #default>
      <pages-programme-slug
        v-if="programme"
        :programme="programme"
      />
    </template>
  </page-layout>
</template>
